/**
 * @file ChefNewOrderModal.tsx
 * @description Rapid order creation modal for kitchen line chefs to enter walk-in or verbal tickets.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Minus, Search, Utensils, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  fetchVenueMenuForChef,
  createDirectChefOrder,
  type ChefMenuItem,
  type ChefMenuCategory,
} from '../services/chefOrderCreation.service';

interface StagedItem {
  item: ChefMenuItem;
  quantity: number;
  notes: string;
}

interface ChefNewOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueId: string;
  onOrderCreated: () => void;
}

export const ChefNewOrderModal: React.FC<ChefNewOrderModalProps> = ({
  isOpen,
  onClose,
  venueId,
  onOrderCreated,
}) => {
  const [tableNumber, setTableNumber] = useState<string>('');
  const [serviceMode, setServiceMode] = useState<'dine-in' | 'takeaway'>('dine-in');
  const [categories, setCategories] = useState<ChefMenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<ChefMenuItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchItemQuery, setSearchItemQuery] = useState<string>('');
  const [stagedItems, setStagedItems] = useState<StagedItem[]>([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !venueId) return;

    let isMounted = true;
    setIsLoadingMenu(true);
    setErrorMessage(null);

    fetchVenueMenuForChef(venueId)
      .then(({ categories: cats, items }) => {
        if (!isMounted) return;
        setCategories(cats);
        setMenuItems(items);
      })
      .catch(err => {
        if (!isMounted) return;
        console.error('[ChefNewOrderModal] Failed to load menu:', err);
        setErrorMessage('Failed to load menu items.');
      })
      .finally(() => {
        if (isMounted) setIsLoadingMenu(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, venueId]);

  const filteredMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      if (selectedCategory !== 'all' && item.category_id !== selectedCategory) {
        return false;
      }
      if (searchItemQuery.trim()) {
        const q = searchItemQuery.toLowerCase().trim();
        return item.name.toLowerCase().includes(q) || item.description.toLowerCase().includes(q);
      }
      return true;
    });
  }, [menuItems, selectedCategory, searchItemQuery]);

  const handleAddItem = (item: ChefMenuItem) => {
    setStagedItems(prev => {
      const existingIndex = prev.findIndex(si => si.item.id === item.id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = {
          ...next[existingIndex],
          quantity: next[existingIndex].quantity + 1,
        };
        return next;
      }
      return [...prev, { item, quantity: 1, notes: '' }];
    });
  };

  const handleUpdateQuantity = (itemId: string, delta: number) => {
    setStagedItems(prev =>
      prev
        .map(si => {
          if (si.item.id === itemId) {
            const nextQty = si.quantity + delta;
            return nextQty > 0 ? { ...si, quantity: nextQty } : null;
          }
          return si;
        })
        .filter((si): si is StagedItem => si !== null)
    );
  };

  const handleUpdateNotes = (itemId: string, notes: string) => {
    setStagedItems(prev =>
      prev.map(si => (si.item.id === itemId ? { ...si, notes } : si))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tableNumber.trim()) {
      setErrorMessage('Please specify a table number or order tag (e.g. Table 4, Takeaway).');
      return;
    }
    if (stagedItems.length === 0) {
      setErrorMessage('Please select at least one item from the menu.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await createDirectChefOrder({
        venueId,
        tableNumber: tableNumber.trim(),
        serviceMode,
        items: stagedItems.map(si => ({
          itemId: si.item.id,
          name: si.item.name,
          price: si.item.price,
          quantity: si.quantity,
          notes: si.notes.trim() || null,
          kitchenStation: si.item.kitchen_station,
        })),
        chefName: 'Chef KDS',
      });

      // Reset and close
      setStagedItems([]);
      setTableNumber('');
      onOrderCreated();
      onClose();
    } catch (err: any) {
      console.error('[ChefNewOrderModal] Order creation failed:', err);
      setErrorMessage(err?.message || 'Failed to dispatch order to kitchen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-3xl bg-[#121212] border border-white/10 rounded-t-3xl sm:rounded-3xl max-h-[92dvh] sm:max-h-[88vh] flex flex-col overflow-hidden shadow-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-0">
        {/* Mobile slide handle */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-zinc-700/60" />
        </div>

        {/* Modal Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between bg-zinc-900/60 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-[#ff4f00]/15 border border-[#ff4f00]/30 flex items-center justify-center text-[#ff4f00]">
              <Utensils className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-black text-white uppercase tracking-tight">
                New Kitchen Ticket
              </h2>
              <p className="text-[11px] text-zinc-400 font-mono">
                Direct entry into kitchen line
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message banner */}
        {errorMessage && (
          <div className="mx-4 mt-3 p-3 rounded-xl bg-rose-950/70 border border-rose-800 text-rose-200 text-xs font-semibold flex items-center gap-2 shrink-0">
            <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
            <span className="flex-1">{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          {/* Top Form Controls: Table / Tag & Service Mode */}
          <div className="p-4 border-b border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-3 shrink-0 bg-black/20">
            <div>
              <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                Table Number / Tag
              </label>
              <input
                type="text"
                required
                autoFocus
                value={tableNumber}
                onChange={e => setTableNumber(e.target.value)}
                placeholder="e.g. Table 4, Bar 2, Walk-in"
                className="w-full min-h-[42px] px-3.5 rounded-xl bg-[#0a0a0a] border border-white/15 text-white placeholder-zinc-500 text-sm font-semibold focus:outline-none focus:border-[#ff4f00] transition-colors"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                Order Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setServiceMode('dine-in')}
                  className={`min-h-[42px] rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    serviceMode === 'dine-in'
                      ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30 font-black'
                      : 'bg-[#181818] border border-white/10 text-zinc-400 hover:text-white'
                  }`}
                >
                  Dine-In
                </button>
                <button
                  type="button"
                  onClick={() => setServiceMode('takeaway')}
                  className={`min-h-[42px] rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    serviceMode === 'takeaway'
                      ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30 font-black'
                      : 'bg-[#181818] border border-white/10 text-zinc-400 hover:text-white'
                  }`}
                >
                  Takeaway
                </button>
              </div>
            </div>
          </div>

          {/* Dual Column Layout: Left Menu Picker, Right Staged Items */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-12 min-h-0 overflow-hidden">
            {/* Left: Menu Items Catalog */}
            <div className="md:col-span-7 border-b md:border-b-0 md:border-r border-white/10 flex flex-col min-h-0">
              {/* Category Pills & Search */}
              <div className="p-3 border-b border-white/10 space-y-2 shrink-0">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                  <input
                    type="text"
                    value={searchItemQuery}
                    onChange={e => setSearchItemQuery(e.target.value)}
                    placeholder="Search menu items..."
                    className="w-full min-h-[38px] pl-9 pr-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#ff4f00]"
                  />
                </div>

                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                  <button
                    type="button"
                    onClick={() => setSelectedCategory('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold shrink-0 transition-colors cursor-pointer ${
                      selectedCategory === 'all'
                        ? 'bg-white/20 text-white'
                        : 'text-zinc-400 hover:text-white bg-white/5'
                    }`}
                  >
                    All
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold shrink-0 transition-colors cursor-pointer ${
                        selectedCategory === cat.id
                          ? 'bg-[#ff4f00] text-white shadow-sm'
                          : 'text-zinc-400 hover:text-white bg-white/5'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {isLoadingMenu ? (
                  <div className="py-12 text-center text-xs text-zinc-500">
                    Loading menu items...
                  </div>
                ) : filteredMenuItems.length === 0 ? (
                  <div className="py-12 text-center text-xs text-zinc-500">
                    No menu items found.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {filteredMenuItems.map(item => {
                      const countInCart =
                        stagedItems.find(si => si.item.id === item.id)?.quantity || 0;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleAddItem(item)}
                          className={`p-2.5 rounded-xl border text-left transition-all flex items-center justify-between gap-2 cursor-pointer ${
                            countInCart > 0
                              ? 'bg-[#ff4f00]/10 border-[#ff4f00]/50'
                              : 'bg-[#181818] border-white/5 hover:border-white/20'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-zinc-200 truncate">{item.name}</p>
                            <span className="text-[10px] text-[#ff4f00] font-mono font-bold">
                              {item.kitchen_station ? `Station: ${item.kitchen_station}` : ''}
                            </span>
                          </div>
                          {countInCart > 0 ? (
                            <span className="w-6 h-6 rounded-lg bg-[#ff4f00] text-white font-mono font-black text-xs flex items-center justify-center shrink-0">
                              {countInCart}
                            </span>
                          ) : (
                            <span className="w-6 h-6 rounded-lg bg-white/5 text-zinc-400 flex items-center justify-center shrink-0">
                              <Plus className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Selected Staged Items */}
            <div className="md:col-span-5 flex flex-col min-h-0 bg-[#0c0c0c]">
              <div className="p-3 border-b border-white/10 flex items-center justify-between shrink-0">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">
                  Ticket Items ({stagedItems.reduce((acc, si) => acc + si.quantity, 0)})
                </span>
                {stagedItems.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setStagedItems([])}
                    className="text-[11px] text-zinc-500 hover:text-rose-400 cursor-pointer"
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {stagedItems.length === 0 ? (
                  <div className="py-12 text-center text-zinc-600 text-xs flex flex-col items-center">
                    <Utensils className="w-8 h-8 mb-2 stroke-1 opacity-50" />
                    <span>Tap items on the left to add to ticket.</span>
                  </div>
                ) : (
                  stagedItems.map(si => (
                    <div
                      key={si.item.id}
                      className="p-2.5 rounded-xl bg-[#141414] border border-white/10 space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-white truncate flex-1">
                          {si.item.name}
                        </span>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleUpdateQuantity(si.item.id, -1)}
                            className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300 cursor-pointer"
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="font-mono font-bold text-xs text-white min-w-[20px] text-center">
                            {si.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUpdateQuantity(si.item.id, 1)}
                            className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300 cursor-pointer"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <input
                        type="text"
                        value={si.notes}
                        onChange={e => handleUpdateNotes(si.item.id, e.target.value)}
                        placeholder="Notes (e.g. No onion, well done)"
                        className="w-full text-[11px] px-2.5 py-1 rounded-lg bg-black/40 border border-white/5 text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-white/20"
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="p-4 border-t border-white/10 bg-zinc-900/60 flex items-center justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="min-h-[44px] px-4 py-2 rounded-xl text-xs font-bold text-zinc-400 hover:text-white transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || stagedItems.length === 0}
              className={`min-h-[44px] px-6 py-2 rounded-xl bg-[#ff4f00] hover:bg-[#e64700] text-white text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-[#ff4f00]/30 transition-all cursor-pointer ${
                isSubmitting || stagedItems.length === 0
                  ? 'opacity-50 pointer-events-none'
                  : 'active:scale-[0.98]'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>{isSubmitting ? 'Creating...' : 'Submit to Kitchen'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
